import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  ArrowLeft, FileText, Search, Plus, Home, Package, 
  Settings, LogOut, Clock, Send, CheckCircle, XCircle
} from "lucide-react";
import { toast } from "sonner";

const QuotesList = ({ user, API }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  const statusFilter = searchParams.get("status") || "all";
  
  useEffect(() => {
    fetchQuotes();
  }, [statusFilter]);
  
  const fetchQuotes = async () => {
    setLoading(true);
    try {
      let url = `${API}/quotes`;
      if (statusFilter && statusFilter !== "all") {
        url += `?status=${statusFilter}`;
      }
      
      const response = await fetch(url, {
        credentials: "include"
      });
      
      if (!response.ok) throw new Error("Failed to fetch quotes");
      
      const data = await response.json();
      setQuotes(data);
    } catch (error) {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };
  
  const handleStatusChange = (newStatus) => {
    if (newStatus === "all") {
      searchParams.delete("status");
    } else {
      searchParams.set("status", newStatus);
    }
    setSearchParams(searchParams);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
      navigate("/", { replace: true });
    } catch (error) {
      navigate("/", { replace: true });
    }
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-secondary text-secondary-foreground",
      sent: "bg-blue-500/20 text-blue-400",
      accepted: "bg-green-500/20 text-green-400",
      rejected: "bg-destructive/20 text-destructive"
    };
    return styles[status] || styles.draft;
  };
  
  const getStatusIcon = (status) => {
    const icons = {
      draft: <Clock className="w-4 h-4" />,
      sent: <Send className="w-4 h-4" />,
      accepted: <CheckCircle className="w-4 h-4" />,
      rejected: <XCircle className="w-4 h-4" />
    };
    return icons[status] || icons.draft;
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const filteredQuotes = quotes.filter(q => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      q.customer_name?.toLowerCase().includes(query) ||
      q.job_description?.toLowerCase().includes(query) ||
      q.quote_id?.toLowerCase().includes(query)
    );
  });
  
  const getPageTitle = () => {
    switch (statusFilter) {
      case "draft": return "Draft Quotes";
      case "sent": return "Sent Quotes";
      case "accepted": return "Accepted Quotes";
      case "rejected": return "Rejected Quotes";
      default: return "All Quotes";
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold">{getPageTitle()}</h1>
                <p className="text-sm text-muted-foreground">
                  {filteredQuotes.length} quote{filteredQuotes.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <nav className="flex items-center gap-2">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <Home className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/materials">
                <Button variant="ghost" size="icon">
                  <Package className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/settings">
                <Button variant="ghost" size="icon">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              data-testid="search-quotes-input"
              placeholder="Search quotes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-input/50 border-white/10"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-full sm:w-48 bg-input/50 border-white/10" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Quotes</SelectItem>
              <SelectItem value="draft">Drafts</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            onClick={() => navigate("/quote/new")}
            className="bg-primary text-primary-foreground"
            data-testid="new-quote-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Quote
          </Button>
        </div>

        {/* Quotes List */}
        <Card className="bg-card border-white/5">
          <CardContent className="pt-6">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-20 bg-secondary/50 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : filteredQuotes.length > 0 ? (
              <div className="space-y-3">
                {filteredQuotes.map((quote) => (
                  <div
                    key={quote.quote_id}
                    data-testid={`quote-item-${quote.quote_id}`}
                    className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/quote/${quote.quote_id}`)}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="hidden sm:flex w-10 h-10 bg-secondary rounded-lg items-center justify-center text-muted-foreground group-hover:text-primary transition-colors">
                        {getStatusIcon(quote.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <h3 className="font-semibold truncate">{quote.customer_name}</h3>
                          <Badge className={getStatusBadge(quote.status)}>
                            {quote.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground mono">
                            #{quote.quote_id?.slice(-6)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {quote.job_description}
                        </p>
                      </div>
                    </div>
                    <div className="text-right ml-4 shrink-0">
                      <p className="font-bold mono text-lg text-primary">
                        {formatCurrency(quote.total)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(quote.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  {searchQuery 
                    ? "No quotes match your search" 
                    : `No ${statusFilter !== 'all' ? statusFilter : ''} quotes yet`
                  }
                </p>
                <Button
                  onClick={() => navigate("/quote/new")}
                  className="bg-primary text-primary-foreground"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Quote
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default QuotesList;
